const BACKEND_API = process.env.REACT_APP_AI_FORM_BUILDER;

export const ASSISTANT_API_URL = `${BACKEND_API}/api/ai`;

export const SCOPE = Object.freeze({
  FORM_BUILDER:     ["form", "formCondition"],
  WORKFLOW_BUILDER: ["workflow", "workflowCondition", "form", "formCondition"],
});

const clean = (v) => (typeof v === "string" ? v.trim() : "");

/**
 * Build a chat-friendly message for a read-only "list" action.
 *
 * The AI returns the items as the result `data` (an array, or an object
 * wrapping one under `data`). Nothing is mutated — we render each item's
 * title (and optional description) as a numbered list. Returns a `skipped`
 * outcome so the dispatcher reports it as informational (no mutation, no
 * "changes applied" badge).
 *
 * @param {Array|Object} data            The list result payload.
 * @param {Object} [opts]
 * @param {(item) => string} [opts.getTitle]   Extract the display title from an item.
 * @param {(item) => string} [opts.getDesc]    Extract the optional description.
 * @param {string} [opts.singular]      Noun for one item, e.g. "condition", "form".
 * @param {string} [opts.plural]        Noun for many items, e.g. "conditions".
 * @param {string} [opts.emptyMessage]  Copy shown when the list is empty.
 * @returns {{ ok: boolean, skipped: boolean, message: string }}
 */
function listMessage(data, { getTitle, getDesc, singular, plural, emptyMessage } = {}) {
  const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

  if (list.length === 0) {
    return { ok: true, skipped: true, message: emptyMessage };
  }

  const formatted = list
    .map((item, i) => {
      const title = clean(getTitle?.(item)) || `Untitled ${singular}`;
      const desc = clean(getDesc?.(item));
      return desc ? `${i + 1}. ${title}\n${desc}` : `${i + 1}. ${title}`;
    })
    .join("\n\n");

  const heading =
    list.length === 1 ? `Here is 1 ${singular}:` : `Here are ${list.length} ${plural}:`;

  return { ok: true, skipped: true, message: `${heading}\n\n${formatted}` };
}

/**
 * List message for the "formCondition" / "workflowCondition" domains.
 * @param {Array|Object} data
 * @param {string} [scope] "workflow" | "form" — only tweaks the empty-state copy.
 */
export function listConditionsMessage(data, scope = "workflow") {
  return listMessage(data, {
    getTitle: (c) => c?.name,
    getDesc: (c) => c?.description,
    singular: "condition",
    plural: "conditions",
    emptyMessage: `There are no conditions on this ${scope} yet.`,
  });
}

/**
 * List message for the "form" domain.
 * @param {Array|Object} data
 */
export function listFormsMessage(data) {
  return listMessage(data, {
    getTitle: (f) => f?.formName || f?.name || f?.label || f?.formHeader?.label,
    getDesc: (f) => f?.description || f?.formHeader?.description,
    singular: "form",
    plural: "forms",
    emptyMessage: "There are no forms yet.",
  });
}
